/**
 * DarkSkiesCardWidget.ts
 *
 * Dedicated stargazing and dark skies forecast trend widget.
 * Features:
 * - Bortle Scale site darkness classification banner and zenith sky brightness.
 * - Diurnal Moon & Solar Phase Horizon Bar with illumination badges and NOW marker.
 * - Dual-curve stargazing trend chart: Diurnal Darkness Wave (% area) and Dark Skies Score (0-10 curve).
 * - Ephemeris & stargazing status footer.
 */

import { Chart } from 'chart.js/auto';
import * as SunCalc from 'suncalc';
import { getMoonPhaseDetails } from '../../../../utils/astronomy';
import { ForecastBinaryService } from '../../../../services/ForecastBinaryService';
import TierAccessService from '../../../../services/TierAccessService';
import { ForecastWidgetConfig, WidgetRenderContext, WidgetRenderer } from '../types';

export function getTimelineStartMs(ctx: WidgetRenderContext): number {
  if (typeof ctx.data?.timelineStartMs === 'number' && !isNaN(ctx.data.timelineStartMs)) {
    return ctx.data.timelineStartMs;
  }
  const tz = ctx.data?.timezone || 'UTC';
  const dateStr = ctx.dateStr || new Date().toISOString().split('T')[0];
  const [y, m, d] = dateStr.split('-').map(Number);
  const localMidnightUtc = Date.UTC(y, m - 1, d, 0, 0, 0);
  const sampleDate = new Date(localMidnightUtc);
  const tzFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric', month: 'numeric', day: 'numeric',
    hour: 'numeric', minute: 'numeric', second: 'numeric',
    hour12: false
  });
  const parts = tzFormatter.formatToParts(sampleDate);
  const partMap: Record<string, string> = {};
  parts.forEach(p => partMap[p.type] = p.value);
  const localAsUtc = Date.UTC(parseInt(partMap.year), parseInt(partMap.month) - 1, parseInt(partMap.day), parseInt(partMap.hour), parseInt(partMap.minute));
  const offsetMs = sampleDate.getTime() - localAsUtc;
  return localMidnightUtc + offsetMs;
}

export class DarkSkiesCardWidget implements WidgetRenderer {
  private bortleDescriptions: Record<number, string> = {
    1: 'Excellent Dark Sky Site',
    2: 'Typical Truly Dark Site',
    3: 'Rural Sky',
    4: 'Rural / Suburban Transition',
    5: 'Suburban Sky',
    6: 'Bright Suburban Sky',
    7: 'Suburban / Urban Transition',
    8: 'City Sky',
    9: 'Inner-City Sky'
  };

  private calculateDarkSkyScore(sunAlt: number, moonAlt: number, moonFraction: number, bortle: number): number {
    const baseScore = Math.max(1.0, Math.min(10.0, 10.0 - (bortle - 1.0) * 1.1));
    let moonPenalty = 0;
    if (moonAlt > 0) {
      moonPenalty = moonFraction * Math.sqrt(Math.sin(moonAlt * Math.PI / 180)) * 7.0;
    }
    let sunPenalty = 0;
    if (sunAlt > -18.0) {
      if (sunAlt > 0) {
        sunPenalty = 10.0;
      } else {
        const factor = (sunAlt + 18.0) / 18.0;
        sunPenalty = Math.pow(factor, 0.75) * 10.0;
      }
    }
    return Math.max(0.0, Math.min(10.0, baseScore - moonPenalty - sunPenalty));
  }

  private buildTimelineTrackHtml(ctx: WidgetRenderContext, totalSteps: number, _widgetId: string): string {
    const lat = ctx.lat || 45.0;
    const lon = ctx.lon || -120.0;
    const data = ctx.data;
    const labels = data.timeLabels || [];
    const steps = Math.min(totalSteps, Math.max(1, labels.length));
    const timelineStartMs = getTimelineStartMs(ctx);

    const colorStops: string[] = [];
    const overnightSteps: Array<{ idx: number; pct: number; sunAlt: number; date: Date }> = [];

    for (let i = 0; i < steps; i++) {
      const stepDate = new Date(timelineStartMs + i * 3600 * 1000);
      const pct = steps > 1 ? (i / (steps - 1)) * 100 : 0;

      let sunAltDeg = 0;
      let moonAltDeg = 0;
      let moonFraction = 0.5;

      try {
        const sPos = SunCalc.getPosition(stepDate, lat, lon);
        sunAltDeg = (typeof sPos.altitude === 'number') ? sPos.altitude : 0;
        const mPos = SunCalc.getMoonPosition(stepDate, lat, lon);
        moonAltDeg = (typeof mPos.altitude === 'number') ? mPos.altitude : 0;
        const mIllum = SunCalc.getMoonIllumination(stepDate);
        moonFraction = mIllum.fraction;
      } catch {
        const hr = stepDate.getHours();
        sunAltDeg = Math.sin((hr - 6) / 12 * Math.PI) * 60;
        moonAltDeg = Math.cos((hr - 6) / 12 * Math.PI) * 45;
      }

      let color = '#facc15'; // Day
      if (sunAltDeg > -0.83) {
        color = '#facc15'; // Daylight
      } else if (sunAltDeg > -6.0) {
        color = '#f97316'; // Civil Twilight / Golden
      } else if (sunAltDeg > -12.0) {
        color = '#2563eb'; // Nautical Twilight / Blue Hour
      } else {
        if (moonAltDeg <= 0) {
          color = '#030712'; // Pitch black darkness
        } else {
          const greyVal = Math.round(35 + moonFraction * 145);
          color = `rgb(${greyVal}, ${greyVal}, ${Math.min(255, greyVal + 25)})`;
        }
        overnightSteps.push({ idx: i, pct, sunAlt: sunAltDeg, date: stepDate });
      }

      colorStops.push(`${color} ${pct.toFixed(2)}%`);
    }

    // Overnight moon phase badges grouped into consecutive nocturnal blocks
    const moonBadgesHtml: string[] = [];
    if (overnightSteps.length > 0) {
      const nightBlocks: Array<Array<{ idx: number; pct: number; sunAlt: number; date: Date }>> = [];
      let currentBlock: Array<{ idx: number; pct: number; sunAlt: number; date: Date }> = [];
      overnightSteps.forEach(s => {
        if (currentBlock.length === 0 || s.idx === currentBlock[currentBlock.length - 1].idx + 1) {
          currentBlock.push(s);
        } else {
          nightBlocks.push(currentBlock);
          currentBlock = [s];
        }
      });
      if (currentBlock.length > 0) nightBlocks.push(currentBlock);

      nightBlocks.forEach(group => {
        let minStep = group[0];
        group.forEach(s => {
          if (s.sunAlt < minStep.sunAlt) minStep = s;
        });
        const phase = getMoonPhaseDetails(minStep.date);
        const illumStr = phase.illumination ? phase.illumination.replace(/\s*Illumination/i, '').trim() : '';
        moonBadgesHtml.push(`
          <div style="position: absolute; left: ${minStep.pct.toFixed(2)}%; top: 50%; transform: translate(-50%, -50%); display: inline-flex; align-items: center; gap: 3px; background: rgba(15, 23, 42, 0.85); border: 1px solid rgba(255,255,255,0.2); border-radius: 999px; padding: 1px 5px; font-size: 0.65rem; color: #fff; pointer-events: none; z-index: 2;" title="${phase.name} (${illumStr})">
            <span>${phase.icon}</span>
            <span style="font-size: 0.6rem; opacity: 0.9;">${illumStr}</span>
          </div>
        `);
      });
    }

    const gradientBg = colorStops.length > 0 ? `linear-gradient(to right, ${colorStops.join(', ')})` : '#facc15';

    return `
      <div style="margin: 6px 0 10px 0;">
        <div style="display: flex; justify-content: space-between; align-items: center; font-size: 0.66rem; color: #94a3b8; margin-bottom: 4px;">
          <span style="font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;">Moon & Solar Phase</span>
          <div style="display: flex; gap: 8px; font-size: 0.62rem;">
            <span style="display: inline-flex; align-items: center; gap: 3px;"><span style="width: 6px; height: 6px; border-radius: 50%; background: #facc15;"></span>Day</span>
            <span style="display: inline-flex; align-items: center; gap: 3px;"><span style="width: 6px; height: 6px; border-radius: 50%; background: #f97316;"></span>Golden</span>
            <span style="display: inline-flex; align-items: center; gap: 3px;"><span style="width: 6px; height: 6px; border-radius: 50%; background: #2563eb;"></span>Blue</span>
            <span style="display: inline-flex; align-items: center; gap: 3px;"><span style="width: 6px; height: 6px; border-radius: 50%; background: #030712; border: 1px solid #475569;"></span>Night</span>
          </div>
        </div>
        <div style="position: relative; width: 100%; height: 10px; border-radius: 6px; background: ${gradientBg}; border: 1px solid rgba(255,255,255,0.12); box-shadow: inset 0 1px 3px rgba(0,0,0,0.5);">
          ${moonBadgesHtml.join('')}
        </div>
      </div>
    `;
  }

  public renderHtml(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): string {
    const opts = widget.options || {};
    const showTrend = opts.showTrend !== false;
    const showPhase = opts.showSolarLunarPhase !== false;
    const chartHeight = opts.chartHeight || 120;
    const bortle = opts.bortleOverride || 2;
    const bortleDesc = this.bortleDescriptions[bortle] || 'Dark Sky Site';
    const zenithMag = (21.99 - (bortle - 1) * 0.45).toFixed(2);

    const horizonDays = ctx.viewDays;
    const rawHours = String(horizonDays).toLowerCase() === 'max'
      ? (ctx.data.timeLabels?.length || 240)
      : (parseInt(String(horizonDays), 10) || 1) * 24;
    const hours = ctx.data.timeLabels && ctx.data.timeLabels.length > 0
      ? Math.min(ctx.data.timeLabels.length, rawHours)
      : rawHours;

    const bortleBannerHtml = `
      <div style="background: rgba(15, 23, 42, 0.65); border: 1px solid rgba(56, 189, 248, 0.25); border-left: 3px solid #38bdf8; border-radius: 8px; padding: 7px 12px; margin-bottom: 8px; display: flex; justify-content: space-between; align-items: center;">
        <div style="display: flex; align-items: center; gap: 8px;">
          <span style="font-size: 0.68rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px;">Bortle Scale:</span>
          <span style="font-size: 0.86rem; font-weight: 800; color: #38bdf8;">Class ${bortle}</span>
          <span style="font-size: 0.74rem; color: #cbd5e1;">· ${bortleDesc}</span>
        </div>
        <div style="font-size: 0.68rem; color: #64748b; font-family: monospace;">
          Zenith: ${zenithMag} mag/arcsec²
        </div>
      </div>
    `;

    const phaseTrackHtml = showPhase ? this.buildTimelineTrackHtml(ctx, hours, widget.id) : '';

    let chartHtml = '';
    if (showTrend) {
      chartHtml = `
        <div style="display: flex; justify-content: space-between; align-items: center; margin: 6px 0 2px 0; font-size: 0.66rem; color: #94a3b8;">
          <div style="display: flex; gap: 12px; align-items: center;">
            <span style="display: inline-flex; align-items: center; gap: 4px;">
              <span style="width: 8px; height: 8px; border-radius: 2px; background: rgba(56, 189, 248, 0.4); border: 1px solid #38bdf8;"></span>
              <span style="color: #38bdf8;">Darkness Wave (Left %)</span>
            </span>
            <span style="display: inline-flex; align-items: center; gap: 4px;">
              <span style="width: 10px; height: 3px; border-radius: 2px; background: #a855f7;"></span>
              <span style="color: #c084fc; font-weight: 600;">Dark Skies Score (Right 0-10)</span>
            </span>
          </div>
          <span style="font-size: 0.60rem; color: #64748b;">Pitch-Black Windows (Sun ≤ -18°)</span>
        </div>
        <div style="height: ${chartHeight}px; position: relative; width: 100%; margin: 4px 0 6px 0;">
          <canvas id="canvas-${widget.id}"></canvas>
        </div>
      `;
    }

    const data = ctx.data;
    const moonName = data.moonName || 'Waxing Crescent';
    const moonIllum = data.moonIllumination || '4%';
    const astroDusk = data.astroDuskTime || '8:04 PM';
    const astroDawn = data.astroDawnTime || '5:42 AM';

    return `
      <div class="metric-card" style="background: rgba(30, 41, 59, 0.75); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 14px; padding: 14px 16px; box-shadow: 0 8px 24px rgba(0,0,0,0.3);">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
          <span style="font-size: 0.95rem; font-weight: 700; color: #fff;">✨ ${widget.title || 'Dark Skies Forecast Trend'}</span>
          <span style="background: rgba(56, 189, 248, 0.15); color: #38bdf8; border: 1px solid rgba(56, 189, 248, 0.3); font-size: 0.65rem; font-weight: 700; padding: 2px 7px; border-radius: 4px;">Bortle Class ${bortle}</span>
        </div>
        ${bortleBannerHtml}
        ${phaseTrackHtml}
        ${chartHtml}
        <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255,255,255,0.06); font-size: 0.72rem; color: #94a3b8;">
          <span>Moon: <strong style="color: #fff;">${moonName} (${moonIllum})</strong></span>
          <div style="display: flex; gap: 12px;">
            <span>Astro Dusk: <strong style="color: #fbbf24;">${astroDusk}</strong></span>
            <span>Astro Dawn: <strong style="color: #38bdf8;">${astroDawn}</strong></span>
          </div>
        </div>
      </div>
    `;
  }

  public init(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): void {
    const canvas = document.getElementById(`canvas-${widget.id}`) as HTMLCanvasElement;
    if (!canvas) return;

    const opts = widget.options || {};
    if (opts.showTrend === false) return;

    if (!ctx.data.hasBinaryPointData && !TierAccessService.userHasLayerAccess('darkskies')) {
      const parent = canvas.parentElement;
      if (parent) {
        canvas.style.display = 'none';
        parent.innerHTML = TierAccessService.buildTrendChartDenialHtml({
          denial: ctx.data.accessDenial || ForecastBinaryService.lastAccessDenial,
          requiredTier: 'pro',
          featureLabel: 'dark skies stargazing score trend charts',
          compact: true
        });
      }
      return;
    }

    const horizonDays = ctx.viewDays;
    const rawHours = String(horizonDays).toLowerCase() === 'max'
      ? (ctx.data.timeLabels?.length || 240)
      : (parseInt(String(horizonDays), 10) || 1) * 24;
    const hours = ctx.data.timeLabels && ctx.data.timeLabels.length > 0
      ? Math.min(ctx.data.timeLabels.length, rawHours)
      : rawHours;

    const labels = (ctx.data.timeLabels || []).slice(0, hours);
    const lat = ctx.lat || 45.0;
    const lon = ctx.lon || -120.0;
    const bortle = opts.bortleOverride || 2;

    const timelineStartMs = getTimelineStartMs(ctx);

    // 1. Compute theoretical Diurnal Darkness Wave (0-100%)
    const diurnalScores: number[] = [];
    const sunAltitudes: number[] = [];
    for (let i = 0; i < hours; i++) {
      const stepDate = new Date(timelineStartMs + i * 3600 * 1000);
      let sunAltDeg = 0;
      let moonAltDeg = 0;
      let moonFraction = 0.5;

      try {
        const sPos = SunCalc.getPosition(stepDate, lat, lon);
        sunAltDeg = (typeof sPos.altitude === 'number') ? sPos.altitude : 0;
        const mPos = SunCalc.getMoonPosition(stepDate, lat, lon);
        moonAltDeg = (typeof mPos.altitude === 'number') ? mPos.altitude : 0;
        const mIllum = SunCalc.getMoonIllumination(stepDate);
        moonFraction = mIllum.fraction;
      } catch {
        const hr = stepDate.getHours();
        sunAltDeg = Math.sin((hr - 6) / 12 * Math.PI) * 60;
        moonAltDeg = Math.cos((hr - 6) / 12 * Math.PI) * 45;
      }

      sunAltitudes.push(sunAltDeg);
      const rawScore = this.calculateDarkSkyScore(sunAltDeg, moonAltDeg, moonFraction, bortle);
      diurnalScores.push(Math.round(rawScore * 10)); // Scale to 0-100
    }

    // 2. Resolve forecasted Dark Skies Score (0.0 - 10.0) from data
    // Map scores strictly by hour, keeping 0.0 during daylight hours to ensure perfect phase alignment
    const scores: number[] = [];
    const forecastHourly = ctx.data.darkskiesScores || [];

    for (let i = 0; i < hours; i++) {
      const sunAlt = sunAltitudes[i] !== undefined ? sunAltitudes[i] : 0;
      if (sunAlt > -0.83) {
        // Daylight: astronomical dark skies score is 0.0
        scores.push(0.0);
      } else if (forecastHourly[i] !== null && forecastHourly[i] !== undefined && !isNaN(forecastHourly[i]!)) {
        // Use real model forecast score for this hour
        const raw = forecastHourly[i]!;
        const val = raw > 10.0 ? (raw / 10.0) : raw;
        scores.push(Number(Math.max(0.0, Math.min(10.0, val)).toFixed(1)));
      } else {
        // Fallback to diurnal theoretical score divided by 10 during darkness
        const theoretical = (diurnalScores[i] !== undefined ? diurnalScores[i] / 10 : 8.0);
        scores.push(Number(Math.max(0.0, Math.min(10.0, theoretical)).toFixed(1)));
      }
    }

    const chartCtx = canvas.getContext('2d');
    let gradDiurnal: any = 'rgba(56, 189, 248, 0.15)';
    if (chartCtx) {
      const g = chartCtx.createLinearGradient(0, 0, 0, opts.chartHeight || 120);
      g.addColorStop(0.0, 'rgba(0, 50, 255, 0.45)');
      g.addColorStop(0.3, 'rgba(0, 200, 200, 0.30)');
      g.addColorStop(0.6, 'rgba(34, 197, 94, 0.20)');
      g.addColorStop(0.85, 'rgba(245, 158, 11, 0.10)');
      g.addColorStop(1.0, 'rgba(180, 0, 0, 0.03)');
      gradDiurnal = g;
    }

    const chart = new Chart(canvas, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Diurnal Darkness Wave (%)',
            data: diurnalScores,
            yAxisID: 'y',
            borderColor: '#38bdf8',
            backgroundColor: gradDiurnal,
            borderWidth: 1.5,
            fill: true,
            tension: 0.35,
            pointRadius: 0,
            pointHoverRadius: 4,
            order: 2
          },
          {
            label: 'Dark Skies Score (0-10)',
            data: scores,
            yAxisID: 'y1',
            borderColor: '#c084fc',
            backgroundColor: 'transparent',
            borderWidth: 2,
            tension: 0.35,
            pointRadius: 0,
            pointHoverRadius: 4,
            order: 1
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: {
          mode: 'index',
          intersect: false
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (context) => {
                if (context.datasetIndex === 0) {
                  return `Diurnal Darkness: ${context.parsed.y}%`;
                }
                return `Dark Skies Score: ${(context.parsed.y ?? 0).toFixed(1)} / 10`;
              }
            }
          }
        },
        scales: {
          x: {
            grid: { color: 'rgba(255, 255, 255, 0.04)' },
            ticks: {
              color: '#94a3b8',
              font: { size: 9 },
              maxRotation: 0,
              autoSkip: true,
              maxTicksLimit: 12
            }
          },
          y: {
            position: 'left',
            min: 0,
            max: 100,
            grid: { color: 'rgba(255, 255, 255, 0.04)' },
            ticks: {
              color: '#38bdf8',
              font: { size: 8 },
              stepSize: 25,
              callback: (v) => `${v}%`
            }
          },
          y1: {
            position: 'right',
            min: 0,
            max: 10,
            grid: { drawOnChartArea: false },
            ticks: {
              color: '#c084fc',
              font: { size: 8 },
              stepSize: 2
            }
          }
        }
      }
    });

    ctx.charts.push(chart);
  }
}
